import { DSVRowArray } from 'd3';
import {
  DashboardDataTransformer,
  KEuroToEuroValueDataTransformer,
  PercentageValueDataTransformer,
} from '../../data_transformers/dashboard_data_transformer';
import { kEurShort } from 'src/app/shared/utils/k-eur-short';
import { toNumDE } from 'src/app/shared/utils/to-num-de';
import { kpiColumn, KpiColumnContext, KpiMeasure } from './kpi-columns';
import { Metric } from '../dashboard-config.model';
import { counterpartKpi, KpiOption, selectedKpi } from './kpi-options';

/** Row key written by {@link KpiLabelDataTransformer}. */
export const KPI_NAME_KEY = 'kpi_name';

/**
 * Copies the selected KPI's column for a measure into a canonical row key
 * (e.g. `value`, `value_change_pct`). Replaces the per-metric option → column maps.
 */
export class KpiColumnDataTransformer implements DashboardDataTransformer {
  constructor(private config: {
    measure: KpiMeasure;
    resultColumn: string;
    context?: KpiColumnContext;
    /** Read the selected type under another metric (dossier count ↔ revenue). */
    fromMetric?: Metric;
  }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const selected = selectedKpi(filterValues);
    const kpi = selected && this.config.fromMetric !== undefined
      ? counterpartKpi(selected, this.config.fromMetric)
      : selected;
    const column = kpi ? kpiColumn(kpi, this.config.measure, this.config.context) : undefined;

    return data.map((row) => ({
      ...row,
      [this.config.resultColumn]: (column && row[column]) ?? '',
    })) as unknown as DSVRowArray;
  }
}

/** Writes the selected KPI's label into `kpi_name`. Replaces the per-metric display-name transformers. */
export class KpiLabelDataTransformer implements DashboardDataTransformer {
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const label = selectedKpi(filterValues)?.label ?? '';
    return data.map((row) => {
      row[KPI_NAME_KEY] = label;
      return row;
    }) as DSVRowArray;
  }
}

/**
 * Formats a value column by the selected KPI's unit: k€ abbreviation, or
 * German number format with an optional unit for counts.
 */
export class KpiValueDisplayDataTransformer implements DashboardDataTransformer {
  constructor(private config: { valueColumnKey: string; resultColumnKey: string; countUnit?: string }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const isKEur = selectedKpi(filterValues)?.unit === 'kEur';
    return data.map((row) => {
      const value = row[this.config.valueColumnKey];
      row[this.config.resultColumnKey] = isKEur
        ? kEurShort(value)
        : toNumDE(value) + ' ' + (this.config.countUnit ?? '');
      return row;
    }) as DSVRowArray;
  }
}

/**
 * Export formatting for every KPI of a metric (exports carry all KPI columns,
 * not only the selected one): k€ → € on value columns, percentages on the
 * given percentage measures.
 */
export class KpiExportDataTransformer implements DashboardDataTransformer {
  private readonly transformers: DashboardDataTransformer[];

  constructor(config: { kpis: KpiOption[]; percentageMeasures: KpiMeasure[]; context?: KpiColumnContext }) {
    this.transformers = config.kpis.flatMap((kpi) => [
      ...(kpi.unit === 'kEur' ? [new KEuroToEuroValueDataTransformer(kpi.valueColumn)] : []),
      ...config.percentageMeasures.map(
        (measure) => new PercentageValueDataTransformer(kpiColumn(kpi, measure, config.context)),
      ),
    ]);
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    return this.transformers.reduce(
      (rows, transformer) => transformer.transform(rows, filterValues),
      data,
    );
  }
}
